import {
  Add16,
  Attachment16,
  FolderAdd16,
  RecentlyViewed16,
  Upload16,
} from '@carbon/icons-react';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileAttachment } from 'api/attachments';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import { CustomerPortalWidgetType } from 'api/customer-portal/types';
import { UploadFileWithProgress } from 'api/files/types';
import { ReactComponent as Folder16 } from 'assets/svgs/folder-filled.svg';
import { SpotlightMessage20 } from 'components/atoms/icons/spotlight-message';
import { toastService } from 'components/molecules/toast';
import { CreateGroupFormProps } from 'components/organisms/files/create-group/modal';
import { UploadFileFormProps } from 'components/organisms/projects/files/file-upload-modal';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import {
  getProjectFileGroupsQueryKey,
  useCreateProjectFileGroupMutation,
  useLinkFilesToProjectsMutation,
  useUploadProjectFileMutation,
} from 'queries/project-files';
import { getRandomColor } from 'utils/get-random-color';
import { queueMacrotask } from 'utils/macrotask';
import { isCustomerPortalInBuilderMode } from 'utils/portal-utils';
import { CpButton } from '../../components/cp-button';
import { CpMenu } from '../../components/modal/cp-menu';
import { CpPopover } from '../../components/modal/cp-popover';
import {
  Sidebar,
  SideBarItem,
  SideBarItemIcon,
  SideBarItemName,
  SideBarSection,
  SideBarSectionTitle,
  SideBarDivider,
} from '../common/side-bar';
import { CPCreateFolderAndUploadFileModal } from './cp-create-folder-and-upload-file-modal';
import { CpUploadFilesToFolderModal } from './cp-upload-files-to-folder-modal';
import {
  CpAllFilesProps,
  GENERAL_SHARED_FILES_FOLDER_ID,
  PROJECT_FILES_FOLDER_ID,
  RECENTS_FOLDER_ID,
  SPOTLIGHT_FOLDER_ID,
} from './types';

const AddButton = styled(CpButton)`
  align-items: center;
  justify-content: center;
  padding-left: 0 !important;
  margin: 0 var(--cp-spacing-05);
  width: calc(100% - calc(var(--cp-spacing-05) * 2));

  /* & * {
    transform: translateX(-50%);
  } */
`;

export const CpFilesSidePane: React.FC<
  Pick<
    CpAllFilesProps,
    | 'search'
    | 'currentFolder'
    | 'onFolderClick'
    | 'data'
    | 'projectId'
    | 'refetch'
  >
> = ({ search, currentFolder, onFolderClick, data, projectId, refetch }) => {
  const isResponsive = useIsTablet();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const translate = useTranslate();

  const [_filesInProgress, setFilesInProgress] = useState<{
    [id: string]: UploadFileWithProgress;
  }>({});

  const { mutateAsync: createFileGroup, isLoading: isCreateFolderLoading } =
    useCreateProjectFileGroupMutation(['project_id', projectId]);

  const { mutateAsync: uploadFile, isLoading: isUploadingFile } =
    useUploadProjectFileMutation();

  const { mutateAsync: linkFilesToProjects, isLoading: isLinkingLoading } =
    useLinkFilesToProjectsMutation(['project_id', projectId]);

  const onFileUploadSuccess = useCallback((fileUid) => {
    queueMacrotask(() => {
      setFilesInProgress(({ [fileUid]: _, ...rest }) => ({
        ...rest,
      }));
    });
  }, []);

  const postUploadFiles = useCallback(
    async (groupId: number, fileIds: number[], fileUids: string[]) => {
      await linkFilesToProjects({
        groupId,
        fileIds,
      });

      fileUids.forEach(onFileUploadSuccess);

      queryClient.refetchQueries(
        getProjectFileGroupsQueryKey(['project_id', projectId]),
      );
      queryClient.refetchQueries({
        predicate: (query) => {
          return (
            query.queryHash.includes('render-input') &&
            query?.queryHash.includes(CustomerPortalWidgetType.AllFiles)
          );
        },
      });
      refetch();
    },
    [linkFilesToProjects, onFileUploadSuccess, projectId, refetch],
  );

  const uploadFiles = useCallback(
    (files: UploadFile[], folderId: number) => {
      return Promise.allSettled(
        files.map(async (file): Promise<{
          file: FileAttachment;
          uid: string;
        }> => {
          setFilesInProgress((prev) => ({
            ...prev,
            [file.uid]: { ...file, progress: 1 },
          }));
          const newFile = await uploadFile({
            file,
            data: {
              projectId: projectId[1],
              folderId,
            },
            reqConfig: {
              // onUploadProgress: (event) => onUploadProgress(event, file.uid),
            },
          });

          return { file: newFile, uid: file.uid };
        }),
      );
    },
    [uploadFile, projectId],
  );

  const onCreateGroupSubmit = useCallback(
    async (formValues: CreateGroupFormProps) => {
      try {
        const newGroup = await createFileGroup(formValues);

        queryClient.refetchQueries({
          predicate: (query) => {
            return (
              query.queryHash.includes('render-input') &&
              query?.queryHash.includes(CustomerPortalWidgetType.AllFiles)
            );
          },
        });

        toastService({
          title: translate('project.folder.create.success.title'),
          message: translate('project.folder.create.success.message', {
            folderName: newGroup.folderName,
          }),
        }).success();

        try {
          const uploadResult = await uploadFiles(
            formValues.files,
            newGroup.folderId,
          );

          toastService({
            title: translate('project.files.upload.success.title'),
            message: translate('project.files.upload.success.message', {
              filesCount: uploadResult.length,
              folderName: newGroup.folderName,
            }),
          }).success();

          const fileIds: number[] = [];
          const fileUids: string[] = [];

          uploadResult.forEach((res) => {
            if (res.status === 'fulfilled') {
              fileIds.push(res.value.file.attachmentId);
              fileUids.push(res.value.uid);
            }
          });

          postUploadFiles(newGroup.folderId, fileIds, fileUids);
        } catch (e) {
          toastService({
            title: translate('project.files.upload.error.title'),
            message: translate('project.files.upload.error.message'),
          }).error();
        }
      } catch (e) {
        toastService({
          title: translate('project.folder.create.error.title'),
          message: translate('project.folder.create.error.message'),
        }).error();
      } finally {
        setIsCreateFolderModalOpen(false);
      }
    },
    [createFileGroup, translate, uploadFiles, postUploadFiles],
  );

  const onUploadFileSubmit = useCallback(
    async (formValues: UploadFileFormProps) => {
      const { group, files } = formValues;

      try {
        const uploadResult = await uploadFiles(files, Number(group.value));

        toastService({
          title: translate('project.files.upload.success.title'),
          message: translate('project.files.upload.success.message', {
            filesCount: uploadResult.length,
            folderName: group.folderName,
          }),
        }).success();

        const fileIds: number[] = [];
        const fileUids: string[] = [];

        uploadResult.forEach((res) => {
          if (res.status === 'fulfilled') {
            fileIds.push(res.value.file.attachmentId);
            fileUids.push(res.value.uid);
          }
        });

        postUploadFiles(Number(group.value), fileIds, fileUids);
      } catch (e) {
        toastService({
          title: translate('project.files.upload.error.title'),
          message: translate('project.files.upload.error.message'),
        }).error();
      } finally {
        setIsUploadModalOpen(false);
      }
    },
    [postUploadFiles, translate, uploadFiles],
  );

  const content = useMemo(
    () => (
      <>
        <Sidebar>
          <CpPopover
            closeOnSelect
            height="120px"
            placement="bottom"
            className="cp-all-files-new-popover"
            content={
              <CpMenu selectedKeys={[]}>
                <CpMenu.Item
                  data-cy="cp.all-files.upload-option"
                  icon={<Upload16 />}
                  onClick={() => {
                    if (isCustomerPortalInBuilderMode()) {
                      return;
                    }
                    setIsUploadModalOpen(true);
                  }}
                >
                  <FormattedMessage id="cp.upload..." />
                </CpMenu.Item>
                <CpMenu.Item
                  data-cy="cp.all-files.create-folder-option"
                  icon={<FolderAdd16 />}
                  onClick={() => {
                    if (isCustomerPortalInBuilderMode()) {
                      return;
                    }
                    setIsCreateFolderModalOpen(true);
                  }}
                >
                  <FormattedMessage id="cp.project.files.create.folder.button..." />
                </CpMenu.Item>
              </CpMenu>
            }
          >
            <AddButton
              data-cy="cp.all-files.new-button"
              cpType={CustomerPortalButtonType.Primary}
              icon={<Add16 />}
            >
              <FormattedMessage id="new" />
            </AddButton>
          </CpPopover>
          <SideBarSection>
            <SideBarItem
              data-cy="cp.all-files.spotlight-folder"
              active={currentFolder?.folderId === SPOTLIGHT_FOLDER_ID}
              onClick={() => onFolderClick('spotlight')}
            >
              <SideBarItemIcon>
                <SpotlightMessage20 />
              </SideBarItemIcon>
              <SideBarItemName>
                <FormattedMessage id="spotlight" />
              </SideBarItemName>
            </SideBarItem>
            <SideBarItem
              active={currentFolder?.folderId === RECENTS_FOLDER_ID}
              onClick={() => onFolderClick('recents')}
              data-cy="cp.all-files.recents-folder"
            >
              <SideBarItemIcon>
                <RecentlyViewed16 />
              </SideBarItemIcon>
              <SideBarItemName>
                <FormattedMessage id="recents" />
              </SideBarItemName>
            </SideBarItem>
          </SideBarSection>

          <SideBarSection>
            <SideBarSectionTitle>
              <FormattedMessage id="folders" />
            </SideBarSectionTitle>
            <SideBarItem
              data-cy="cp.all-files.project-files-folder"
              active={currentFolder?.folderId === PROJECT_FILES_FOLDER_ID}
              onClick={() => onFolderClick('project_files')}
            >
              <SideBarItemIcon>
                <Attachment16 />
              </SideBarItemIcon>
              <SideBarItemName>
                <FormattedMessage
                  id="project.task.attachments.title"
                  values={{ count: data?.attachmentsInProject?.length ?? 0 }}
                />
              </SideBarItemName>
            </SideBarItem>

            <SideBarItem
              active={
                currentFolder?.folderId === GENERAL_SHARED_FILES_FOLDER_ID
              }
              onClick={() => onFolderClick('general_shared_files')}
              data-cy="cp.all-files.shared-folder"
            >
              <SideBarItemIcon>
                <RecentlyViewed16 />
              </SideBarItemIcon>
              <SideBarItemName>
                <FormattedMessage id="project.files.shared.group.title" />
              </SideBarItemName>
            </SideBarItem>
            <SideBarDivider />

            {data?.folders
              ?.filter?.((e) => !e?.isDefault)
              ?.map?.((folder) => {
                if (
                  isResponsive &&
                  !!search?.trim() &&
                  !folder.folderName
                    .toLowerCase()
                    .includes(search.toLowerCase())
                ) {
                  return <></>;
                }

                return (
                  <SideBarItem
                    data-cy={`cp.all-files.folder-${folder.folderName}`}
                    active={currentFolder?.folderId === folder.folderId}
                    key={folder.folderId}
                    onClick={() => onFolderClick(folder.folderId)}
                  >
                    <SideBarItemIcon color={getRandomColor(folder.folderName)}>
                      <Folder16 />
                    </SideBarItemIcon>
                    <SideBarItemName
                      title={folder.folderName}
                      data-cy={`cp.all-files.folder-${folder.folderName}.name`}
                    >
                      {folder.folderName}
                    </SideBarItemName>
                  </SideBarItem>
                );
              })}
          </SideBarSection>
          {isCreateFolderModalOpen ? (
            <CPCreateFolderAndUploadFileModal
              isCreatingGroup={isCreateFolderLoading || isLinkingLoading}
              onClose={() => {
                setIsCreateFolderModalOpen(false);
                setIsUploadModalOpen(false);
              }}
              onSubmit={onCreateGroupSubmit}
              projectId={projectId}
              folders={data?.folders}
            />
          ) : (
            <> </>
          )}
          {isUploadModalOpen ? (
            <CpUploadFilesToFolderModal
              isUploadingFile={isUploadingFile || isLinkingLoading}
              onClose={() => setIsUploadModalOpen(false)}
              projectId={projectId}
              folders={data?.folders}
              onSubmit={onUploadFileSubmit}
            />
          ) : (
            <></>
          )}
        </Sidebar>
      </>
    ),
    [
      currentFolder?.folderId,
      data?.attachmentsInProject?.length,
      data?.folders,
      isCreateFolderModalOpen,
      isCreateFolderLoading,
      isLinkingLoading,
      onCreateGroupSubmit,
      projectId,
      isUploadModalOpen,
      isUploadingFile,
      onUploadFileSubmit,
      onFolderClick,
      isResponsive,
      search,
    ],
  );

  if (isResponsive) {
    return content;
  }

  return <>{!search?.trim() ? content : <></>}</>;
};
